// src/components/ClientDashboardPage.js
import React, { useState, useEffect, useCallback } from 'react';
import axiosInstance from '../api/axiosInstance';
import { motion } from 'framer-motion';
import CountUp from 'react-countup';
import {
  Grid,
  Typography,
  Box,
  CircularProgress,
  Alert,
  Chip,
  Card,
  Stack,
} from '@mui/material';
import PageHeader from './common/PageHeader';
import {
  ResponsiveContainer,
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  BarChart,
  Bar,
} from 'recharts';

import ForumOutlinedIcon from '@mui/icons-material/ForumOutlined';
import SyncAltOutlinedIcon from '@mui/icons-material/SyncAltOutlined';
import QuestionAnswerOutlinedIcon from '@mui/icons-material/QuestionAnswerOutlined';
import DataUsageOutlinedIcon from '@mui/icons-material/DataUsageOutlined';
import AccessTimeRoundedIcon from '@mui/icons-material/AccessTimeRounded';
import CallSplitRoundedIcon from '@mui/icons-material/CallSplitRounded';
import AutoAwesomeRoundedIcon from '@mui/icons-material/AutoAwesomeRounded';
import PersonOutlineRoundedIcon from '@mui/icons-material/PersonOutlineRounded';

// =================================================================
// نظام تصميم موحّد مبني على هوية Meta Cloud (أزرق سماوي)
// =================================================================
const T = {
  ink: '#0f172a',
  inkSoft: '#334155',
  muted: '#64748b',
  faint: '#94a3b8',
  border: '#e9eef5',
  surface: '#ffffff',
  canvas: '#f4f7fb',
  brand: '#0ea5e9',
  radius: 20,
  shadow: '0 1px 2px rgba(15,23,42,0.04), 0 8px 24px rgba(15,23,42,0.06)',
  shadowHover: '0 2px 4px rgba(15,23,42,0.06), 0 16px 40px rgba(14,165,233,0.14)',
};

// لوحة فئوية متناسقة (هوية زرقاء + ألوان متجانسة)
const C = {
  sky: { main: '#0ea5e9', soft: '#e6f6fe', ink: '#0369a1' },
  emerald: { main: '#10b981', soft: '#e4faf1', ink: '#047857' },
  violet: { main: '#7c6cf6', soft: '#efecfe', ink: '#5b45d6' },
  amber: { main: '#f59e0b', soft: '#fef3d9', ink: '#b45309' },
  rose: { main: '#f43f5e', soft: '#ffe9ec', ink: '#be123c' },
  slate: { main: '#64748b', soft: '#eef2f7', ink: '#334155' },
};

const nf = (n) => Number(n || 0).toLocaleString('en-US');

// =================================================================
// بطاقة مؤشّر رئيسي (KPI) — تصميم نظيف وموحّد
// =================================================================
const KpiCard = ({ title, subtitle, value, icon, tone, delay = 0 }) => (
  <motion.div
    initial={{ opacity: 0, y: 18 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ duration: 0.45, delay, ease: 'easeOut' }}
    whileHover={{ y: -4 }}
    style={{ height: '100%' }}
  >
    <Card
      elevation={0}
      sx={{
        height: '100%',
        p: 2.75,
        borderRadius: `${T.radius}px`,
        bgcolor: T.surface,
        border: `1px solid ${T.border}`,
        boxShadow: T.shadow,
        transition: 'box-shadow .25s ease, border-color .25s ease',
        position: 'relative',
        overflow: 'hidden',
        '&:hover': { boxShadow: T.shadowHover, borderColor: `${tone.main}55` },
      }}
    >
      {/* شريط لوني علوي رفيع */}
      <Box sx={{ position: 'absolute', insetInlineStart: 0, top: 0, bottom: 0, width: 4, bgcolor: tone.main, opacity: 0.9 }} />

      <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 2 }}>
        <Box
          sx={{
            width: 46, height: 46, borderRadius: 3,
            display: 'grid', placeItems: 'center',
            bgcolor: tone.soft, color: tone.ink,
            '& svg': { fontSize: 24 },
          }}
        >
          {icon}
        </Box>
        <Box sx={{ minWidth: 0 }}>
          <Typography sx={{ color: T.inkSoft, fontWeight: 700, fontSize: '0.9rem', lineHeight: 1.2 }}>
            {title}
          </Typography>
          {subtitle && (
            <Typography sx={{ color: T.faint, fontSize: '0.72rem', mt: 0.25 }}>
              {subtitle}
            </Typography>
          )}
        </Box>
      </Stack>

      <Typography
        sx={{
          color: T.ink, fontWeight: 800, lineHeight: 1,
          fontSize: { xs: '1.9rem', sm: '2.15rem' },
          fontFamily: 'Cairo, sans-serif', letterSpacing: '-0.02em',
        }}
      >
        <CountUp end={Number(value || 0)} duration={1.6} separator="," preserveValue />
      </Typography>
    </Card>
  </motion.div>
);

// =================================================================
// بطاقة إحصائية مصغّرة (للتقارير) — نفس لغة التصميم
// =================================================================
const MetricTile = ({ label, value, icon, tone }) => (
  <Card
    elevation={0}
    sx={{
      p: 2.25, height: '100%', borderRadius: `${T.radius}px`,
      bgcolor: T.surface, border: `1px solid ${T.border}`, boxShadow: T.shadow,
    }}
  >
    <Stack direction="row" alignItems="center" spacing={1.25} sx={{ mb: 1.25 }}>
      <Box sx={{ width: 34, height: 34, borderRadius: 2, display: 'grid', placeItems: 'center', bgcolor: tone.soft, color: tone.ink, '& svg': { fontSize: 18 } }}>
        {icon}
      </Box>
      <Typography sx={{ color: T.muted, fontSize: '0.82rem', fontWeight: 600 }}>{label}</Typography>
    </Stack>
    <Typography sx={{ color: T.ink, fontWeight: 800, fontSize: '1.5rem', lineHeight: 1, fontFamily: 'Cairo, sans-serif' }}>
      {value}
    </Typography>
  </Card>
);

// =================================================================
// لوحة رسم بياني موحّدة
// =================================================================
const ChartPanel = ({ title, subtitle, data, children, accent }) => (
  <Card
    elevation={0}
    sx={{
      p: { xs: 2, sm: 3 }, height: '100%', borderRadius: `${T.radius}px`,
      bgcolor: T.surface, border: `1px solid ${T.border}`, boxShadow: T.shadow,
    }}
  >
    <Stack direction="row" alignItems="flex-start" justifyContent="space-between" sx={{ mb: 2.5 }}>
      <Box>
        <Typography sx={{ color: T.ink, fontWeight: 700, fontSize: '1.05rem' }}>{title}</Typography>
        <Typography sx={{ color: T.faint, fontSize: '0.78rem', mt: 0.25 }}>{subtitle}</Typography>
      </Box>
      <Chip
        label={`${data?.length || 0} نقطة`}
        size="small"
        sx={{ bgcolor: accent.soft, color: accent.ink, fontWeight: 700, fontSize: '0.72rem', height: 26 }}
      />
    </Stack>
    <Box sx={{ height: 380 }}>
      {data && data.length > 0 ? (
        <ResponsiveContainer width="100%" height="100%">{children}</ResponsiveContainer>
      ) : (
        <Box sx={{ height: '100%', display: 'grid', placeItems: 'center', textAlign: 'center' }}>
          <Box>
            <Box sx={{ width: 64, height: 64, borderRadius: '50%', bgcolor: T.canvas, display: 'grid', placeItems: 'center', mx: 'auto', mb: 1.5, color: T.faint }}>
              <DataUsageOutlinedIcon sx={{ fontSize: 30 }} />
            </Box>
            <Typography sx={{ color: T.muted, fontWeight: 600 }}>لا توجد بيانات كافية</Typography>
            <Typography sx={{ color: T.faint, fontSize: '0.8rem' }}>ستظهر البيانات هنا عند توفّر نشاط</Typography>
          </Box>
        </Box>
      )}
    </Box>
  </Card>
);

// tooltip موحّد للرسوم
const chartTooltip = (accent) => {
  const ChartTooltipContent = ({ active, payload, label }) => {
    if (!active || !payload || !payload.length) return null;
    const dateLabel = typeof label === 'string' && label.includes('-')
      ? new Date(label).toLocaleDateString('ar-EG', { day: 'numeric', month: 'short' })
      : label;
    return (
      <Box sx={{ bgcolor: '#fff', border: `1px solid ${T.border}`, borderRadius: 2.5, px: 1.75, py: 1.25, boxShadow: '0 10px 30px rgba(15,23,42,0.12)' }}>
        <Typography sx={{ color: T.muted, fontSize: '0.72rem', mb: 0.5 }}>{dateLabel}</Typography>
        <Typography sx={{ color: accent.ink, fontWeight: 800, fontSize: '1rem' }}>{nf(payload[0].value)}</Typography>
      </Box>
    );
  };
  ChartTooltipContent.displayName = 'ChartTooltipContent';
  return ChartTooltipContent;
};

// =================================================================
// المكوّن الرئيسي
// =================================================================
export default function ClientDashboardPage() {
  const [stats, setStats] = useState(null);
  const [tokenChartData, setTokenChartData] = useState([]);
  const [convChartData, setConvChartData] = useState([]);
  const [reportsData, setReportsData] = useState(null);
  const [roiData, setRoiData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [refreshing, setRefreshing] = useState(false);

  const normalizeTokenChartData = (rows = []) =>
    rows.map((item) => {
      const tokensIn = Number(item.tokens_in || 0);
      const tokensOut = Number(item.tokens_out || 0);
      return { ...item, tokens_in: tokensIn, tokens_out: tokensOut, total_tokens: tokensIn + tokensOut };
    });

  const fetchAllData = useCallback(async ({ isRefresh = false } = {}) => {
    if (isRefresh) setRefreshing(true); else setLoading(true);
    setError('');

    const [statsResult, tokensResult, convsResult, reportsResult, roiResult] = await Promise.allSettled([
      axiosInstance.get('/chatbot/stats/'),
      axiosInstance.get('/chatbot/charts/tokens/'),
      axiosInstance.get('/chatbot/charts/conversations/'),
      axiosInstance.get('/chatbot/reports/?days=30'),
      axiosInstance.get('/chatbot/roi/?days=30'),
    ]);

    let hasAnySuccess = false;
    const normalizedTokenData = tokensResult.status === 'fulfilled' ? normalizeTokenChartData(tokensResult.value.data) : [];
    const reportsPayload = reportsResult.status === 'fulfilled' ? reportsResult.value.data : null;

    if (statsResult.status === 'fulfilled') {
      setStats(statsResult.value.data);
      hasAnySuccess = true;
    } else if (reportsPayload) {
      const totalTokensFromChart = normalizedTokenData.reduce((sum, item) => sum + (item.total_tokens || 0), 0);
      setStats({
        total_conversations: reportsPayload.summary?.total_conversations || 0,
        active_conversations: reportsPayload.status_distribution?.reduce((sum, item) => sum + (item.count || 0), 0) || 0,
        total_messages: reportsPayload.summary?.total_messages || 0,
        total_tokens_consumed: totalTokensFromChart,
      });
      hasAnySuccess = true;
    }

    if (tokensResult.status === 'fulfilled') { setTokenChartData(normalizedTokenData); hasAnySuccess = true; }
    else setTokenChartData([]);

    if (convsResult.status === 'fulfilled') { setConvChartData(convsResult.value.data); hasAnySuccess = true; }
    else setConvChartData([]);

    if (reportsPayload) { setReportsData(reportsPayload); hasAnySuccess = true; }
    else setReportsData(null);

    if (roiResult.status === 'fulfilled') { setRoiData(roiResult.value.data); hasAnySuccess = true; }
    else setRoiData(null);

    if (!hasAnySuccess) setError('فشل في تحميل بيانات لوحة التحكم.');
    if (statsResult.status !== 'fulfilled' && !reportsPayload) setStats(null);

    if (isRefresh) setRefreshing(false); else setLoading(false);
  }, []);

  useEffect(() => { fetchAllData(); }, [fetchAllData]);
  const handleRefresh = async () => { await fetchAllData({ isRefresh: true }); };

  const formatResponseTime = (seconds) => {
    if (!seconds) return '0 ث';
    if (seconds < 60) return `${Math.round(seconds)} ث`;
    if (seconds < 3600) return `${Math.round(seconds / 60)} د`;
    return `${Math.round(seconds / 3600)} س`;
  };

  const statusMeta = {
    active_bot: { label: 'مع البوت', tone: C.emerald },
    active_human: { label: 'مع الدعم', tone: C.amber },
    closed: { label: 'مغلق', tone: C.slate },
    pending_department_choice: { label: 'بانتظار', tone: C.sky },
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', height: '80vh', gap: 2.5 }}>
        <CircularProgress size={54} thickness={4} sx={{ color: T.brand }} />
        <Typography sx={{ color: T.muted, fontWeight: 600 }}>جاري تحميل لوحة التحكم…</Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: { xs: 2, md: 4 } }}>
        <Alert severity="error" sx={{ borderRadius: 3, fontWeight: 600 }}>{error}</Alert>
      </Box>
    );
  }

  if (!stats) return null;

  const s = reportsData?.summary || {};

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: T.canvas, pb: 6 }}>
      <PageHeader
        title="لوحة التحكم"
        subtitle="نظرة شاملة على أداء المساعد الذكي وتحليلات الأداء"
        variant="gradient"
        onRefresh={handleRefresh}
        refreshing={refreshing}
      />

      <Box sx={{ px: { xs: 2, sm: 3, md: 4 } }}>
        {/* ============ أثر المساعد الذكي (ROI) — آخر 30 يوم ============ */}
        {roiData && roiData.total_conversations > 0 && (
          <Box sx={{
            mb: 4, p: { xs: 2.5, md: 3.5 }, borderRadius: 4, color: '#fff',
            background: 'linear-gradient(135deg, #0ea5e9 0%, #6366f1 100%)',
            boxShadow: '0 12px 32px rgba(14,165,233,0.28)',
          }}>
            <Typography sx={{ fontWeight: 800, fontSize: '1.05rem', mb: 0.5 }}>
              💡 أثر المساعد الذكي على أعمالك
            </Typography>
            <Typography sx={{ opacity: 0.9, fontSize: '0.82rem', mb: 2.5 }}>
              خلال آخر {roiData.period_days} يوماً
            </Typography>
            <Grid container spacing={2.5}>
              {[
                { v: `${roiData.self_resolution_rate}%`, l: 'حلّها البوت تلقائياً', s: `${roiData.self_handled} من ${roiData.total_conversations} محادثة` },
                { v: `${roiData.staff_hours_saved}`, l: 'ساعة عمل موفّرة (تقديري)', s: `${roiData.bot_replies} رد آلي` },
                { v: `${roiData.after_hours_messages}`, l: 'رسالة خارج أوقات الدوام', s: 'خدمة عملاء 24/7' },
                { v: `${roiData.escalated}`, l: 'حُوّلت لموظف بشري', s: 'الحالات المعقّدة فقط' },
              ].map((k, i) => (
                <Grid xs={6} md={3} key={i}>
                  <Typography sx={{ fontWeight: 800, fontSize: { xs: '1.6rem', md: '2rem' }, lineHeight: 1.1 }}>{k.v}</Typography>
                  <Typography sx={{ fontWeight: 700, fontSize: '0.85rem', mt: 0.5 }}>{k.l}</Typography>
                  <Typography sx={{ opacity: 0.85, fontSize: '0.72rem' }}>{k.s}</Typography>
                </Grid>
              ))}
            </Grid>
          </Box>
        )}

        {/* ============ المؤشّرات الرئيسية ============ */}
        <Grid container spacing={2.5} sx={{ mb: 4 }}>
          <Grid xs={12} sm={6} lg={3}>
            <KpiCard title="إجمالي المحادثات" subtitle="منذ البداية" value={stats.total_conversations} icon={<ForumOutlinedIcon />} tone={C.sky} delay={0} />
          </Grid>
          <Grid xs={12} sm={6} lg={3}>
            <KpiCard title="المحادثات النشطة" subtitle="الآن" value={stats.active_conversations} icon={<SyncAltOutlinedIcon />} tone={C.emerald} delay={0.06} />
          </Grid>
          <Grid xs={12} sm={6} lg={3}>
            <KpiCard title="إجمالي الرسائل" subtitle="مُرسلة ومُستلمة" value={stats.total_messages} icon={<QuestionAnswerOutlinedIcon />} tone={C.violet} delay={0.12} />
          </Grid>
          <Grid xs={12} sm={6} lg={3}>
            <KpiCard title="التوكنز المستهلكة" subtitle="إجمالي الاستهلاك" value={stats.total_tokens_consumed} icon={<DataUsageOutlinedIcon />} tone={C.amber} delay={0.18} />
          </Grid>
        </Grid>

        {/* ============ تحليلات الأداء (آخر 30 يوم) ============ */}
        {reportsData && (
          <Box sx={{ mb: 4 }}>
            <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
              <AutoAwesomeRoundedIcon sx={{ color: T.brand, fontSize: 20 }} />
              <Typography sx={{ color: T.ink, fontWeight: 700, fontSize: '1.05rem' }}>تحليلات الأداء</Typography>
              <Chip label="آخر 30 يوم" size="small" sx={{ bgcolor: C.sky.soft, color: C.sky.ink, fontWeight: 700, height: 24, fontSize: '0.7rem' }} />
            </Stack>

            <Grid container spacing={2.5}>
              <Grid xs={6} md={3}>
                <MetricTile label="متوسط وقت الرد" value={formatResponseTime(s.avg_response_time_seconds)} icon={<AccessTimeRoundedIcon />} tone={C.emerald} />
              </Grid>
              <Grid xs={6} md={3}>
                <MetricTile label="معدل التحويل للدعم" value={`${s.transfer_rate_percent || 0}%`} icon={<CallSplitRoundedIcon />} tone={C.amber} />
              </Grid>
              <Grid xs={6} md={3}>
                <MetricTile label="محادثات جديدة" value={nf(s.new_conversations)} icon={<ForumOutlinedIcon />} tone={C.violet} />
              </Grid>
              <Grid xs={6} md={3}>
                <MetricTile label="رسائل المستخدمين" value={nf(s.total_user_messages)} icon={<PersonOutlineRoundedIcon />} tone={C.sky} />
              </Grid>
            </Grid>
          </Box>
        )}

        {/* ============ توزيع الحالات وأوقات الذروة ============ */}
        {reportsData && (
          <Grid container spacing={2.5} sx={{ mb: 4 }}>
            <Grid xs={12} md={6}>
              <Card elevation={0} sx={{ p: 3, height: '100%', borderRadius: `${T.radius}px`, bgcolor: T.surface, border: `1px solid ${T.border}`, boxShadow: T.shadow }}>
                <Typography sx={{ color: T.ink, fontWeight: 700, mb: 2 }}>توزيع حالات المحادثات</Typography>
                <Stack direction="row" flexWrap="wrap" gap={1.25}>
                  {(reportsData.status_distribution || []).map((item, idx) => {
                    const meta = statusMeta[item.status] || { label: item.status, tone: C.slate };
                    return (
                      <Box key={idx} sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 1.5, py: 0.9, borderRadius: 3, bgcolor: meta.tone.soft }}>
                        <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: meta.tone.main }} />
                        <Typography sx={{ color: meta.tone.ink, fontWeight: 700, fontSize: '0.82rem' }}>{meta.label}</Typography>
                        <Typography sx={{ color: meta.tone.ink, fontWeight: 800, fontSize: '0.82rem' }}>{item.count}</Typography>
                      </Box>
                    );
                  })}
                </Stack>
              </Card>
            </Grid>
            <Grid xs={12} md={6}>
              <Card elevation={0} sx={{ p: 3, height: '100%', borderRadius: `${T.radius}px`, bgcolor: T.surface, border: `1px solid ${T.border}`, boxShadow: T.shadow }}>
                <Typography sx={{ color: T.ink, fontWeight: 700, mb: 2 }}>أوقات الذروة</Typography>
                <Stack direction="row" flexWrap="wrap" gap={1.25}>
                  {(reportsData.peak_hours || []).slice(0, 5).map((item, idx) => (
                    <Box key={idx} sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 1.5, py: 0.9, borderRadius: 3, border: `1px solid ${T.border}`, bgcolor: T.canvas }}>
                      <AccessTimeRoundedIcon sx={{ fontSize: 16, color: T.brand }} />
                      <Typography sx={{ color: T.inkSoft, fontWeight: 700, fontSize: '0.82rem' }}>
                        {String(Math.floor(item.hour)).padStart(2, '0')}:00
                      </Typography>
                      <Typography sx={{ color: T.faint, fontSize: '0.78rem' }}>{item.count} رسالة</Typography>
                    </Box>
                  ))}
                </Stack>
              </Card>
            </Grid>
          </Grid>
        )}

        {/* ============ الرسوم البيانية ============ */}
        <Grid container spacing={2.5}>
          <Grid xs={12}>
            <ChartPanel title="المحادثات النشطة يوميًا" subtitle="آخر 30 يوم" data={convChartData} accent={C.sky}>
              <BarChart data={convChartData} margin={{ top: 10, right: 12, left: 0, bottom: 0 }}>
                <CartesianGrid strokeDasharray="4 4" stroke={T.border} vertical={false} />
                <XAxis dataKey="date" tick={{ fontSize: 11, fill: T.faint }} axisLine={false} tickLine={false} minTickGap={24} />
                <YAxis tick={{ fontSize: 11, fill: T.faint }} axisLine={false} tickLine={false} width={44} allowDecimals={false} />
                <RechartsTooltip content={chartTooltip(C.sky)} cursor={{ fill: C.sky.soft, fillOpacity: 0.5 }} />
                <Bar dataKey="count" fill={C.sky.main} radius={[6, 6, 0, 0]} maxBarSize={34} />
              </BarChart>
            </ChartPanel>
          </Grid>
          <Grid xs={12}>
            <ChartPanel title="استهلاك التوكنز اليومي" subtitle="آخر 30 يوم" data={tokenChartData} accent={C.violet}>
              <AreaChart data={tokenChartData} margin={{ top: 10, right: 12, left: 0, bottom: 0 }}>
                <defs>
                  <linearGradient id="gToken" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor={C.violet.main} stopOpacity={0.28} />
                    <stop offset="100%" stopColor={C.violet.main} stopOpacity={0.02} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="4 4" stroke={T.border} vertical={false} />
                <XAxis dataKey="date" tick={{ fontSize: 11, fill: T.faint }} axisLine={false} tickLine={false} minTickGap={24} />
                <YAxis tick={{ fontSize: 11, fill: T.faint }} axisLine={false} tickLine={false} width={44} />
                <RechartsTooltip content={chartTooltip(C.violet)} cursor={{ stroke: C.violet.main, strokeOpacity: 0.2 }} />
                <Area type="monotone" dataKey="total_tokens" stroke={C.violet.main} strokeWidth={2.5} fill="url(#gToken)" dot={false} activeDot={{ r: 5, strokeWidth: 2, stroke: '#fff', fill: C.violet.main }} />
              </AreaChart>
            </ChartPanel>
          </Grid>
        </Grid>
      </Box>
    </Box>
  );
}
